// Band model: a template's page layout as an ordered list of horizontal
// strips ("bands"), each with its own print rules. See docs/bands-spec.md.
//
// Today's four sections are one fixed band layout (header → items → total →
// footer), so every existing template can be read as bands with
// `getTemplateBands`, and a band list that only uses what the four sections
// can do converts back with `bandsToSections`.
//
// No imports on purpose: this file and bandLayout.js are meant to be usable
// unchanged by the PDF backend (Node), so both sides share one set of rules.

export const LAYOUT_VERSION = 1;

export const LAYOUT_MODES = {
  SECTIONS: "sections",
  BANDS: "bands",
};

export const BAND_TYPES = {
  PAGE_HEADER: "pageHeader",
  DOC_HEADER: "docHeader",
  ITEMS: "items",
  SUMMARY: "summary",
  DOC_FOOTER: "docFooter",
  PAGE_FOOTER: "pageFooter",
};

// On which pages a band prints.
export const BAND_REPEAT = {
  EVERY_PAGE: "everyPage",
  FIRST_PAGE: "firstPage",
  ONCE: "once",
  // Only with placement "pageBottom": pinned to the bottom of the final page.
  LAST_PAGE: "lastPage",
};

// Where a band sits on the page.
export const BAND_PLACEMENT = {
  // Straight after the content above it.
  AFTER_CONTENT: "afterContent",
  // Pinned to the bottom of the page, stacked upwards in list order.
  PAGE_BOTTOM: "pageBottom",
};

// The keys the four-section layout keeps its content under.
export const LEGACY_SECTION_KEYS = ["head", "items", "total", "foot"];

// Defaults per band type; a band's own values win.
const TYPE_DEFAULTS = {
  [BAND_TYPES.PAGE_HEADER]: { repeat: BAND_REPEAT.EVERY_PAGE, placement: BAND_PLACEMENT.AFTER_CONTENT },
  [BAND_TYPES.DOC_HEADER]: { repeat: BAND_REPEAT.FIRST_PAGE, placement: BAND_PLACEMENT.AFTER_CONTENT },
  [BAND_TYPES.ITEMS]: { repeat: BAND_REPEAT.ONCE, placement: BAND_PLACEMENT.AFTER_CONTENT },
  [BAND_TYPES.SUMMARY]: { repeat: BAND_REPEAT.ONCE, placement: BAND_PLACEMENT.AFTER_CONTENT },
  [BAND_TYPES.DOC_FOOTER]: { repeat: BAND_REPEAT.ONCE, placement: BAND_PLACEMENT.AFTER_CONTENT },
  [BAND_TYPES.PAGE_FOOTER]: { repeat: BAND_REPEAT.EVERY_PAGE, placement: BAND_PLACEMENT.PAGE_BOTTOM },
};

// px for a new band with no height of its own.
const DEFAULT_BAND_HEIGHT = 100;

// Page sizes in px at 96 per inch, the unit element positions and band
// heights use (an A4 page is 793.7 × 1122.5).
const PX_PER_MM = 96 / 25.4;
const PAGE_SIZES_MM = {
  a4: { width: 210, height: 297 },
  letter: { width: 215.9, height: 279.4 },
};

export function getPageSizePx(prop) {
  const size = PAGE_SIZES_MM[(prop?.paperSize || "a4").toString().toLowerCase()] || PAGE_SIZES_MM.a4;
  const landscape = prop?.orientation === "landscape";
  return {
    width: (landscape ? size.height : size.width) * PX_PER_MM,
    height: (landscape ? size.width : size.height) * PX_PER_MM,
  };
}

// Reads an on/off setting that may be a boolean or "true"/"false" (same rule
// as isFlagOn in helpers.js, repeated here to keep this file import-free).
function flag(value, defaultValue) {
  if (value === undefined || value === null || value === "") return defaultValue;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return !!value;
}

const isOneOf = (value, values) => Object.values(values).includes(value);

// A band with every field filled in and unknown values replaced by the type's
// defaults. `height` is in px, the same unit as element positions; the items
// band has no height (it takes what its rows need).
export function normalizeBand(band) {
  const type = isOneOf(band?.type, BAND_TYPES) ? band.type : BAND_TYPES.SUMMARY;
  const defaults = TYPE_DEFAULTS[type];
  const height = parseFloat(band?.height);

  return {
    ...band,
    id: band?.id || type,
    type,
    section: band?.section || band?.id || type,
    repeat: isOneOf(band?.repeat, BAND_REPEAT) ? band.repeat : defaults.repeat,
    placement: isOneOf(band?.placement, BAND_PLACEMENT) ? band.placement : defaults.placement,
    height: type === BAND_TYPES.ITEMS ? null : (height >= 0 ? height : DEFAULT_BAND_HEIGHT),
    canGrow: flag(band?.canGrow, false),
  };
}

// Band heights in px from the four-section `heights`, which are shares of the
// page height scaled by their own total (head + body + total + foot), not by
// 100 — the same arithmetic as the PDF backend and the preview.
function sectionHeightsPx(heights, pageHeightPx) {
  const h = heights || {};
  const sum = (Number(h.head) || 0) + (Number(h.body) || 0) + (Number(h.total) || 0) + (Number(h.foot) || 0);
  const px = (value) => (sum > 0 ? pageHeightPx * (Number(value) || 0) / sum : 0);
  return { head: px(h.head), total: px(h.total), foot: px(h.foot) };
}

// The four-section layout of a template written as bands. Each band's
// `section` names the template key holding its content, so nothing moves.
// `pageHeightPx` defaults to the template's paper size and orientation.
export function sectionsToBands(template, { pageHeightPx = getPageSizePx(template?.prop).height } = {}) {
  const heights = sectionHeightsPx(template?.heights, pageHeightPx);
  const repeatHeader = flag(template?.head?.printingHeaderEveryPage, true);
  const repeatFooter = flag(template?.foot?.printingFooterEveryPage, false);
  const totalAboveFooter = template?.total?.position === "aboveFooter";

  return [
    {
      id: "head",
      type: BAND_TYPES.PAGE_HEADER,
      section: "head",
      repeat: repeatHeader ? BAND_REPEAT.EVERY_PAGE : BAND_REPEAT.FIRST_PAGE,
      placement: BAND_PLACEMENT.AFTER_CONTENT,
      height: heights.head,
    },
    {
      id: "items",
      type: BAND_TYPES.ITEMS,
      section: "items",
    },
    {
      id: "total",
      type: BAND_TYPES.SUMMARY,
      section: "total",
      // "Above footer" pins the total to the bottom of the last page, just
      // above the footer; otherwise it follows the last item row.
      repeat: totalAboveFooter ? BAND_REPEAT.LAST_PAGE : BAND_REPEAT.ONCE,
      placement: totalAboveFooter ? BAND_PLACEMENT.PAGE_BOTTOM : BAND_PLACEMENT.AFTER_CONTENT,
      height: heights.total,
    },
    {
      id: "foot",
      type: repeatFooter ? BAND_TYPES.PAGE_FOOTER : BAND_TYPES.DOC_FOOTER,
      section: "foot",
      repeat: repeatFooter ? BAND_REPEAT.EVERY_PAGE : BAND_REPEAT.LAST_PAGE,
      placement: BAND_PLACEMENT.PAGE_BOTTOM,
      height: heights.foot,
    },
  ].map(normalizeBand);
}

export function getLayoutMode(template) {
  return template?.prop?.layoutMode === LAYOUT_MODES.BANDS ? LAYOUT_MODES.BANDS : LAYOUT_MODES.SECTIONS;
}

// The bands a template prints with: its own list in bands mode, otherwise its
// four sections converted.
export function getTemplateBands(template) {
  if (getLayoutMode(template) === LAYOUT_MODES.BANDS && Array.isArray(template?.bands) && template.bands.length > 0) {
    return template.bands.filter(Boolean).map(normalizeBand);
  }
  return sectionsToBands(template);
}

// The four-section settings equivalent to `bands`, or null when the bands
// use something the four sections can't express (another band, another
// order, a growing band, a page-1-only footer, …). `heights` come back as
// percentages of `pageHeightPx` adding up to 100, the body taking the rest.
export function bandsToSections(bands, { pageHeightPx = getPageSizePx().height } = {}) {
  const list = (bands || []).map(normalizeBand);
  if (list.length !== 4) return null;

  const [head, items, total, foot] = list;
  const sameKeys = [head, items, total, foot].every((band, i) => band.section === LEGACY_SECTION_KEYS[i]);
  if (!sameKeys || items.type !== BAND_TYPES.ITEMS) return null;
  if (list.some((band) => band.canGrow)) return null;

  const isTop = (band) => band.placement === BAND_PLACEMENT.AFTER_CONTENT;
  if (!isTop(head) || ![BAND_REPEAT.EVERY_PAGE, BAND_REPEAT.FIRST_PAGE].includes(head.repeat)) return null;

  let totalPosition;
  if (isTop(total) && total.repeat === BAND_REPEAT.ONCE) totalPosition = "afterItems";
  else if (!isTop(total) && total.repeat === BAND_REPEAT.LAST_PAGE) totalPosition = "aboveFooter";
  else return null;

  if (isTop(foot) || ![BAND_REPEAT.EVERY_PAGE, BAND_REPEAT.LAST_PAGE].includes(foot.repeat)) return null;

  const pct = (px) => (px / pageHeightPx) * 100;
  const heights = { head: pct(head.height), total: pct(total.height), foot: pct(foot.height) };
  heights.body = 100 - heights.head - heights.total - heights.foot;
  if (heights.body < 0) return null;

  return {
    printingHeaderEveryPage: head.repeat === BAND_REPEAT.EVERY_PAGE,
    totalPosition,
    printingFooterEveryPage: foot.repeat === BAND_REPEAT.EVERY_PAGE,
    heights,
  };
}

// ---- Dates (spec §8.2 and §8.3) ---------------------------------------------
//
// Patterns use these tokens; anything in [brackets] prints as written, and
// other characters print as they are:
//   YYYY 2026   YY 26
//   MMMM October   MMM Oct   MM 10   M 10
//   DD 05   D 5   dddd Monday   ddd Mon
//   HH 06 (24-hour)   H 6   hh 06 (12-hour)   h 6   A AM/PM   a am/pm
//   mm 18   ss 02

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DATE_TOKENS = /\[([^\]]*)\]|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|hh|h|mm|ss|A|a/g;

const pad2 = (n) => String(n).padStart(2, "0");

// `parts`: { year, month (1-12), day, weekday (0 = Sunday), hour (0-23), minute, second }.
export function formatDateParts(parts, pattern) {
  const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  const values = {
    YYYY: String(parts.year),
    YY: String(parts.year).slice(-2),
    MMMM: MONTHS[parts.month - 1],
    MMM: MONTHS[parts.month - 1].slice(0, 3),
    MM: pad2(parts.month),
    M: String(parts.month),
    DD: pad2(parts.day),
    D: String(parts.day),
    dddd: WEEKDAYS[parts.weekday],
    ddd: WEEKDAYS[parts.weekday].slice(0, 3),
    HH: pad2(parts.hour),
    H: String(parts.hour),
    hh: pad2(hour12),
    h: String(hour12),
    mm: pad2(parts.minute),
    ss: pad2(parts.second),
    A: parts.hour < 12 ? "AM" : "PM",
    a: parts.hour < 12 ? "am" : "pm",
  };
  return String(pattern || "").replace(DATE_TOKENS, (token, literal) => (literal !== undefined ? literal : values[token]));
}

// A date value from the print data: only ISO dates are formatted
// (2026-10-05, optionally with a time after "T" or a space); no time-zone
// conversion. Anything else comes back unchanged.
export function formatIsoDate(value, pattern) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(String(value ?? ""));
  if (!m) return value;
  const [year, month, day] = [m[1], m[2], m[3]].map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return formatDateParts({ year, month, day, weekday, hour: Number(m[4] || 0), minute: Number(m[5] || 0), second: Number(m[6] || 0) }, pattern);
}

// The current date and time element: placeholder field, and its formatter.
// ---- Price-change rows ------------------------------------------------------
// Both layout modes: when the template turns price-change notifications on
// (`prop.isPriceChangeNotification`), an item row whose `IsPriceNotify` is
// true prints in the template's notification colours
// (`prop.priceNotificationColour`): the background behind the whole row (its
// detail line too) and the colour of its text. They replace the row's
// stripe, column backgrounds and font colours.

export const PRICE_NOTIFY_DEFAULT_BACKGROUND = "#A7D8F5FF";

// { background, color } for `row`, or null when it isn't highlighted. An
// empty colour means "unchanged".
export function getPriceNotifyColours(prop, row) {
  if (!prop || !(prop.isPriceChangeNotification === true || prop.isPriceChangeNotification === "true")) return null;
  const flag = row?.IsPriceNotify;
  if (!(flag === true || String(flag).toLowerCase() === "true")) return null;
  const colours = prop.priceNotificationColour || {};
  return { background: colours.backgroundColour || "", color: colours.foreColour || "" };
}

// ---- Currency symbol (docs/bands-spec.md §8.2) -------------------------------

const blank = (value) => value === undefined || value === null || ["", "null", "undefined"].includes(String(value).trim());

// The symbol for an ISO currency code, from the runtime's built-in currency
// data (Intl, the Unicode CLDR): its narrow symbol, the one used in its own
// country ("GBP" £, "LKR" Rs, "INR" ₹, "AUD" $). null when the runtime
// doesn't know the code.
function symbolForCode(code) {
  for (const currencyDisplay of ["narrowSymbol", "symbol"]) {
    try {
      const part = new Intl.NumberFormat("en", { style: "currency", currency: code, currencyDisplay })
        .formatToParts(0)
        .find((p) => p.type === "currency");
      if (part) return part.value;
    } catch (e) {
      // An invalid code, or a runtime without narrowSymbol: try the next.
    }
  }
  return null;
}

// The currency symbol to print: `symbol` when there is one, otherwise the
// symbol for `code` (matched ignoring case), otherwise the code itself in
// capitals ("XYZ"). A code that is already a symbol ("£") is used as it is.
// Empty when there's neither. Blank, null and the text "null"/"undefined"
// count as missing.
export function getCurrencySymbol(symbol, code) {
  if (!blank(symbol)) return String(symbol).trim();
  if (blank(code)) return "";
  const text = String(code).trim();
  if (!/^[A-Za-z]{3}$/.test(text)) return text;
  return symbolForCode(text.toUpperCase()) || text.toUpperCase();
}

// A formatted amount with its currency symbol: before it ("$741.00"), with a
// space when the symbol ends in a letter ("Rs 741.00", "AED 741.00"), or
// after it, always with a space ("741.00 €"). No symbol: the amount alone.
export function joinCurrencySymbol(symbol, amount, position = "before") {
  if (!symbol) return amount;
  if (position === "after") return `${amount} ${symbol}`;
  return /\p{L}$/u.test(symbol) ? `${symbol} ${amount}` : `${symbol}${amount}`;
}

// ---- Column totals ---------------------------------------------------------
// A "totals" element prints the total of one items-table column over all the
// item rows, optionally per unit type ("BOX: 10, EA: 1, PACK: 6"). Like the
// current date & time, it's recognised by `data` and configured on the
// element:
//   columnTotal: { column, splitByUnit, unitField, unitLayout, separator }
// The PDF and the editor's preview both build the text with
// formatColumnTotal(), passing their own number formatting.

export const COLUMN_TOTAL_FIELD = "ColumnTotal";
export const COLUMN_TOTAL_PATH = "System.ColumnTotal";

// The row field holding each list's unit type.
export const UNIT_TYPE_FIELDS = {
  EposSalesFieldsDefintion: "SaleUnitTypeName",
  EposPurchaseOrderFieldsDefintion: "UnitType",
};

// Quantity columns: their totals are split by unit type unless turned off.
export const QUANTITY_COLUMNS = ["SalesQuantity", "OrderQuantity"];

// Rows without a unit type are counted under this word.
export const NO_UNIT_TYPE_LABEL = "UNITS";

export const UNIT_LAYOUTS = {
  UNIT_FIRST: "unitFirst",     // BOX: 10
  NUMBER_FIRST: "numberFirst", // 10 BOX
};

// A row's value for a column: the row's own field, else its CustomArray.
// Numbers and numeric strings ("1,234.50") count; anything else is 0.
export function getRowNumber(row, column) {
  const raw = row && (row[column] !== undefined ? row[column] : row.CustomArray?.[column]);
  if (typeof raw === "number") return isFinite(raw) ? raw : 0;
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return raw === null || raw === undefined || String(raw).trim() === "" || isNaN(n) ? 0 : n;
}

// Sums without floating-point noise (0.1 + 0.2 = 0.3).
const tidy = (n) => Math.round(n * 1e9) / 1e9;

// [{ unit, total }]: one entry per unit type in the order they first appear
// (unit types match ignoring case and surrounding spaces, keeping the first
// spelling), or a single { unit: null, total } when not split.
export function getColumnTotals(rows, { column, splitByUnit = false, unitField = "SaleUnitTypeName" } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!splitByUnit) {
    return [{ unit: null, total: tidy(list.reduce((sum, row) => sum + getRowNumber(row, column), 0)) }];
  }
  const groups = new Map();
  list.forEach((row) => {
    const name = String(row?.[unitField] ?? "").trim() || NO_UNIT_TYPE_LABEL;
    const key = name.toUpperCase();
    if (!groups.has(key)) groups.set(key, { unit: name, total: 0 });
    groups.get(key).total = tidy(groups.get(key).total + getRowNumber(row, column));
  });
  return [...groups.values()];
}

// The element's text value. `formatNumber(n)` applies the element's number
// format (its `format`, spec §8.2); without one, numbers print as they are.
// Split by unit: "BOX: 10, EA: 1" (unitFirst) or "10 BOX, 1 EA" (numberFirst),
// joined with `separator` (", " by default; a line break puts each on its
// own line).
// With no rows, a split total is empty and an unsplit one is 0.
export function formatColumnTotal(rows, config = {}, formatNumber = (n) => String(n)) {
  const totals = getColumnTotals(rows, config);
  if (!config.splitByUnit) return formatNumber(totals[0].total);
  const separator = typeof config.separator === "string" ? config.separator : ", ";
  return totals
    .map(({ unit, total }) => (config.unitLayout === UNIT_LAYOUTS.NUMBER_FIRST
      ? `${formatNumber(total)} ${unit}`
      : `${unit}: ${formatNumber(total)}`))
    .join(separator);
}

// ---- Calculations (docs/bands-spec.md §8.3b) ---------------------------------
// An element that adds and subtracts other elements of its section, and fixed
// numbers: "Net Total + Delivery Charges". Saved as
//   data: "Calculation", text: "{Calculation}",
//   calculation: { terms: [{ op: "add" | "subtract", element: <id> } | { op, value: <number> }] }

export const CALCULATION_FIELD = "Calculation";
export const CALCULATION_PATH = "System.Calculation";
export const CALCULATION_OPS = { ADD: "add", SUBTRACT: "subtract" };

// A number out of a value or text: numbers as they are; in text, the last
// number in it, thousands separators ignored ("£1,234.50" is 1234.5,
// "Delivery 2: 120.00" is 120, "£-5" is -5). null when there's no number.
export function readNumber(value) {
  if (typeof value === "number") return isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const numbers = value.match(/-?(?:\d[\d,]*(?:\.\d+)?|\.\d+)/g);
  return numbers ? Number(numbers[numbers.length - 1].replace(/,/g, "")) : null;
}

const isCustomTextElement = (el) => el.data === "customText" || String(el.name || "").includes("Custom-Text-");

// The number an element stands for in a calculation:
// - another calculation: its result;
// - a column total: the column's total over all rows (not split by unit);
// - custom text: the number in its text;
// - a data field: the number in its raw value, `valueOf(element)`.
// null when there's none.
export function getElementNumber(element, context = {}, seen = new Set()) {
  if (!element) return null;
  if (element.data === CALCULATION_FIELD) return computeCalculation(element, context, seen);
  if (element.data === COLUMN_TOTAL_FIELD) {
    const config = element.columnTotal || {};
    return getColumnTotals(context.rows, { ...config, splitByUnit: false })[0].total;
  }
  if (isCustomTextElement(element)) return readNumber(element.text);
  return readNumber(context.valueOf ? context.valueOf(element) : null);
}

// A calculation element's result.
//   context  { elements: every element of its section (array or keyed
//              object), rows: the item rows (for column totals),
//              valueOf(element): a data field's raw value }
// Terms add or subtract (op "subtract"; anything else adds). A term is an
// element of the section, by `id`, or a fixed `value`. A term with no number
// (missing value, empty text, an element no longer there, a calculation that
// refers back to itself) counts as 0. With no number in any term, or no
// terms, the result is null: the element prints empty.
export function computeCalculation(element, context = {}, seen = new Set()) {
  const terms = (element && element.calculation && element.calculation.terms) || [];
  if (!terms.length || seen.has(element.id)) return null;
  const inner = new Set(seen).add(element.id);
  const list = Array.isArray(context.elements) ? context.elements : Object.values(context.elements || {});
  let total = 0;
  let counted = false;
  terms.forEach((term) => {
    const n = term && term.element !== undefined
      ? getElementNumber(list.find((el) => el && el.id === term.element), context, inner)
      : readNumber(term && term.value);
    if (n === null) return;
    counted = true;
    total += term.op === CALCULATION_OPS.SUBTRACT ? -n : n;
  });
  return counted ? tidy(total) : null;
}

export const CURRENT_DATE_TIME_FIELD = "CurrentDateTime";
export const CURRENT_DATE_TIME_PATH = "System.CurrentDateTime";

// `date` (a Date) as seen in `timeZone` (an IANA name such as
// "Europe/London"; the runtime's own zone when missing or unknown).
export function formatDateTime(date, pattern, timeZone) {
  let zone = timeZone;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
  } catch (e) {
    zone = undefined;
  }
  const fields = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "long",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(date).forEach((part) => {
    fields[part.type] = part.value;
  });
  return formatDateParts({
    year: Number(fields.year),
    month: Number(fields.month),
    day: Number(fields.day),
    weekday: WEEKDAYS.indexOf(fields.weekday),
    hour: Number(fields.hour) % 24,
    minute: Number(fields.minute),
    second: Number(fields.second),
  }, pattern);
}

// ---- Text in bands mode -----------------------------------------------------

// Distance between the lines of multi-line text in bands mode, as a multiple
// of the font size (CSS line-height; in PDFKit, lineGap = this × size − the
// font's own line height). Fixed so the editor and the PDF measure the same.
export const BAND_LINE_HEIGHT = 1.6;

// The same for wrapped item-table cells (spec §6.1), which sit tighter.
export const BAND_TABLE_LINE_HEIGHT = 1.25;

// The order an address object's parts print in, one line each, empty parts
// left out (the PDF's long-standing order).
export const ADDRESS_LINE_ORDER = ["AddressLine1", "AddressLine2", "City", "PostCode", "County", "Country"];

// ---- Items table sizes ------------------------------------------------------

// Sizes of the items table in px, from `items.layout` and the items band's
// detail line (spec §6.3). The preview and the PDF use these numbers, so the
// table breaks across pages in the same places.
//   headerHeight   the heading row
//   tableChrome    heading row + its bottom border + 1px table border top and bottom
//   rowHeight      a one-line item row: `itemRow.rowHeight` when set (never
//                  less than one line plus 2px above and below), else automatic
//   rowPaddingY    space above the first line of a row's cells (10px when
//                  automatic; with a set height the line is centred)
//   wrapLineHeight each extra line a wrapped cell adds
//   detailHeight   the detail line under each row (0 without one)
//   rowLineSize    the line drawn under each row (0 when off)
export function getTableMetrics(layout, detailRow = null) {
  const { tableHeader = {}, itemRow = {} } = layout || {};
  const ptToPx = 96 / 72;
  const rowFontPx = (Number(itemRow.fontSize) || 8) * ptToPx;

  const headerHeight = Math.max(32, Math.ceil((Number(tableHeader.fontSize) || 8) * ptToPx * 1.25 + 20));
  const headerBorderSize = tableHeader.showBottomBorder ? Number(tableHeader.bottomBorderSize ?? 1) : 0;
  const lineHeight = Math.ceil(rowFontPx * BAND_TABLE_LINE_HEIGHT);
  const setRowHeight = getSetRowHeight(itemRow);
  const rowHeight = setRowHeight
    ? Math.max(lineHeight + 4, setRowHeight)
    : Math.max(41, Math.ceil(rowFontPx * 1.25 + 20));
  const rowPaddingY = setRowHeight ? Math.floor((rowHeight - lineHeight) / 2) : 10;
  const rowLineSize = itemRow.showHorizontalLines ? Number(itemRow.horizontalLineSize) || 1 : 0;

  let detailHeight = 0;
  if (detailRow) {
    const chip = detailRow.style === "chips" ? detailRow.chipStyle || {} : {};
    const detailFontPx = (Number(detailRow.font?.fontSize) || 7) * ptToPx;
    detailHeight = Math.ceil(detailFontPx * 1.25 + 2 * ((Number(chip.paddingY) || 0) + (Number(chip.borderWidth) || 0)) + 6);
  }

  return {
    headerHeight,
    headerBorderSize,
    tableChrome: headerHeight + headerBorderSize + 2,
    rowHeight,
    rowPaddingY,
    wrapLineHeight: lineHeight,
    detailHeight,
    rowLineSize,
    rowPitch: rowHeight + rowLineSize,
  };
}

// The item row height set in the editor (`items.layout.itemRow.rowHeight`,
// px), or 0 for automatic.
export function getSetRowHeight(itemRow) {
  const n = Math.round(Number(itemRow?.rowHeight));
  return n > 0 ? n : 0;
}

// Corner radius of the items table's outer border in px
// (`items.layout.tableBorderRadius`), both layout modes. Templates saved
// before it existed use 4, as the editor always drew them.
export const DEFAULT_TABLE_BORDER_RADIUS = 4;

export function getTableBorderRadius(layout) {
  const n = Number(layout?.tableBorderRadius);
  return layout?.tableBorderRadius === undefined || layout?.tableBorderRadius === "" || isNaN(n)
    ? DEFAULT_TABLE_BORDER_RADIUS
    : Math.max(0, n);
}

// A whole item row in bands mode: the row (taller by one wrapLineHeight for
// each extra line its tallest wrapped cell needs), its detail line and the
// line under it.
export function getItemRowHeight(metrics, lines = 1) {
  return metrics.rowHeight + (Math.max(1, lines) - 1) * metrics.wrapLineHeight + metrics.detailHeight + metrics.rowLineSize;
}

// ---- Page geometry in bands mode ------------------------------------------

export const PAGE_FRAME_DEFAULTS = {
  show: true,
  borderColor: "#E5E7EBFF",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: 0,
  padding: 0,
};

// The page frame with defaults filled in, or null when there is none.
export function getPageFrame(template) {
  const raw = template?.pageFrame;
  if (!raw || !flag(raw.show, true)) return null;
  const size = (value, fallback) => {
    const n = parseFloat(value);
    return n >= 0 ? n : fallback;
  };
  return {
    ...PAGE_FRAME_DEFAULTS,
    ...raw,
    show: true,
    borderWidth: size(raw.borderWidth, PAGE_FRAME_DEFAULTS.borderWidth),
    borderStyle: BORDER_STYLES.includes(raw.borderStyle) ? raw.borderStyle : PAGE_FRAME_DEFAULTS.borderStyle,
    borderSides: raw.borderSides || {},
    borderRadius: size(raw.borderRadius, PAGE_FRAME_DEFAULTS.borderRadius),
    padding: size(raw.padding, PAGE_FRAME_DEFAULTS.padding),
  };
}

export const PAGE_FRAME_SIDES = ["top", "right", "bottom", "left"];
const BORDER_STYLES = ["solid", "dashed", "dotted"];

// One side of the page frame: { color, width, style }. Like a shape's border,
// the frame has values for all sides and optional per-side overrides in
// `borderSides.{top,right,bottom,left}.{color,width,style}`.
export function getPageFrameSide(pageFrame, side) {
  const override = pageFrame?.borderSides?.[side] || {};
  const width = parseFloat(override.width);
  return {
    color: override.color || pageFrame.borderColor,
    width: width >= 0 ? width : pageFrame.borderWidth,
    style: BORDER_STYLES.includes(override.style) ? override.style : pageFrame.borderStyle,
  };
}

// Where things sit on a page in bands mode, in px from the paper's top-left:
//   page     the paper
//   frame    the page minus the margins (the frame's outer edge, if any)
//   content  where bands are laid out: the frame minus each side's border
//            and the padding
// Without a frame, content is the page minus the margins.
export function getPageLayout(template) {
  const page = getPageSizePx(template?.prop);
  const margin = template?.prop?.margin || {};
  const m = (side) => (Number(margin[side]) || 0) * 96;

  const frame = { top: m("top"), left: m("left"), width: page.width - m("left") - m("right"), height: page.height - m("top") - m("bottom") };
  const pf = getPageFrame(template);
  if (!pf) return { page, frame, content: { ...frame }, pageFrame: null };

  // Each side's own border width (sides can differ).
  const width = (side) => getPageFrameSide(pf, side).width;
  const content = {
    top: frame.top + width("top") + pf.padding,
    left: frame.left + width("left") + pf.padding,
    width: Math.max(0, frame.width - width("left") - width("right") - 2 * pf.padding),
    height: Math.max(0, frame.height - width("top") - width("bottom") - 2 * pf.padding),
  };
  return { page, frame, content, pageFrame: pf };
}

// ---- Section rules (the editor's wording) ----------------------------------

// Longest name a user can give an added section.
export const SECTION_NAME_MAX = 40;

// What the editor offers for "where does this section print". Above the
// items table only the top rules make sense; below it, the others.
export const SECTION_RULES = {
  topEveryPage: { label: "Top of every page", region: "top", placement: BAND_PLACEMENT.AFTER_CONTENT, repeat: BAND_REPEAT.EVERY_PAGE, type: BAND_TYPES.PAGE_HEADER },
  topFirstPage: { label: "Top of the first page only", region: "top", placement: BAND_PLACEMENT.AFTER_CONTENT, repeat: BAND_REPEAT.FIRST_PAGE, type: BAND_TYPES.DOC_HEADER },
  afterContent: { label: "Once, after what's above it", region: "bottom", placement: BAND_PLACEMENT.AFTER_CONTENT, repeat: BAND_REPEAT.ONCE, type: BAND_TYPES.SUMMARY },
  bottomEveryPage: { label: "Bottom of every page", region: "bottom", placement: BAND_PLACEMENT.PAGE_BOTTOM, repeat: BAND_REPEAT.EVERY_PAGE, type: BAND_TYPES.PAGE_FOOTER },
  bottomLastPage: { label: "Bottom of the last page", region: "bottom", placement: BAND_PLACEMENT.PAGE_BOTTOM, repeat: BAND_REPEAT.LAST_PAGE, type: BAND_TYPES.DOC_FOOTER },
  bottomFirstPage: { label: "Bottom of the first page", region: "bottom", placement: BAND_PLACEMENT.PAGE_BOTTOM, repeat: BAND_REPEAT.FIRST_PAGE, type: BAND_TYPES.DOC_FOOTER },
};

// "top" for a position above the items band, "bottom" below it.
export function getSectionRegion(bands, index) {
  const itemsIndex = (bands || []).findIndex((band) => band.type === BAND_TYPES.ITEMS);
  return itemsIndex >= 0 && index < itemsIndex ? "top" : "bottom";
}

export function getRulesForRegion(region) {
  return Object.keys(SECTION_RULES).filter((key) => SECTION_RULES[key].region === region);
}

// The rule a band follows; anything unexpected reads as the region's first.
export function getSectionRule(band, region) {
  const key = Object.keys(SECTION_RULES).find((k) =>
    SECTION_RULES[k].region === region && SECTION_RULES[k].placement === band.placement && SECTION_RULES[k].repeat === band.repeat);
  return key || getRulesForRegion(region)[0];
}

export function applySectionRule(band, ruleKey) {
  const rule = SECTION_RULES[ruleKey];
  if (!rule || band.type === BAND_TYPES.ITEMS) return band;
  return normalizeBand({ ...band, placement: rule.placement, repeat: rule.repeat, type: rule.type });
}

// Moves a band up or down the list; one that lands in the other region
// (above/below the items) takes that region's first rule.
export function moveBand(bands, id, delta) {
  const index = bands.findIndex((band) => band.id === id);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= bands.length) return bands;
  const next = [...bands];
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((band, i) => {
    if (band.type === BAND_TYPES.ITEMS) return band;
    const region = getSectionRegion(next, i);
    const rule = getSectionRule(band, region);
    return SECTION_RULES[rule] && (SECTION_RULES[rule].placement !== band.placement || SECTION_RULES[rule].repeat !== band.repeat)
      ? applySectionRule(band, rule)
      : band;
  });
}

// Settings only the bands format can express, besides the band list itself.
export function usesBandOnlyFeatures(template) {
  if (getPageFrame(template)) return true;
  const bands = getTemplateBands(template);
  if (bands.some((band) => band.canGrow || (band.type === BAND_TYPES.ITEMS && band.detailRow))) return true;
  if (Object.values(template?.items?.items || {}).some((column) => column && column.wrap)) return true;
  if (getSetRowHeight(template?.items?.layout?.itemRow)) return true;
  return bands.some((band) => {
    const content = template?.[band.section] || {};
    const placed = [
      ...["elements", "companyProps", "shapes", "images", "qrCodes"].flatMap((path) => Object.values(content[path] || {})),
      content.logo, content.documentTitle, content.VatSummary,
    ].filter((item) => item && typeof item === "object");
    return placed.some((item) => item.collapseEmpty || item.keepInRow || item.stretchWithContent || item.matchRow || item.followLeft || item.followRight || (item.showWhen && item.showWhen.op));
  });
}

// ---- Rotated lines -----------------------------------------------------------

// A line shape's two ends and the box around them, in px relative to its
// section (docs/bands-spec.md §8.6). `rotation` is in degrees, clockwise,
// and the line turns around its start: the middle of its left end, at
// (left, top + thickness / 2). Its length is `style.width` and its thickness
// `style.height`.
//
// Returns { x1, y1, x2, y2, thickness, box: { top, left, width, height } }.
// `box` is what to pass to layoutBandContent(); a level line's box is its own
// designed box. Draw the line from (x1, y1) to (x2, y2), moved by however far
// layoutBandContent() moved the box.
export function getLineGeometry(shape) {
  const px = (value) => parseFloat(value) || 0;
  const style = (shape && shape.style) || {};
  const left = px(style.left);
  const top = px(style.top);
  const length = px(style.width);
  const thickness = px(style.height) || 1;
  const raw = Number(shape && shape.rotation);
  const turned = Number.isFinite(raw) ? ((raw % 360) + 360) % 360 : 0;
  const angle = turned > 180 ? turned - 360 : turned;

  const x1 = left;
  const y1 = top + thickness / 2;
  if (!angle) {
    return { x1, y1, x2: left + length, y2: y1, thickness, box: { top, left, width: length, height: thickness } };
  }
  const rad = (angle * Math.PI) / 180;
  const x2 = tidy(x1 + length * Math.cos(rad));
  const y2 = tidy(y1 + length * Math.sin(rad));
  return {
    x1,
    y1,
    x2,
    y2,
    thickness,
    box: {
      top: tidy(Math.min(y1, y2) - thickness / 2),
      left: tidy(Math.min(x1, x2)),
      width: tidy(Math.abs(x2 - x1)),
      height: tidy(Math.abs(y2 - y1) + thickness),
    },
  };
}

// ---- Editing --------------------------------------------------------------

// Content of a new band: the same fields as the footer section.
export function createBandSection() {
  return {
    elements: {},
    shapes: { border: { color: "#333333FF", size: 1, fillColor: "#FFFFFFFF" } },
    qrCodes: {},
    images: {},
    font: { fontFamily: "Inter", fontSize: 10, color: "#000000FF" },
    background: { showColor: false, color: "#FFFFFFFF" },
  };
}

// A new band of `type`, with an id (also its content key) not yet in `bands`.
export function createBand(type, bands = []) {
  const taken = new Set(bands.flatMap((band) => [band.id, band.section]));
  let n = 1;
  while (taken.has(`band${n}`)) n++;
  const id = `band${n}`;
  return normalizeBand({ id, type, section: id, height: DEFAULT_BAND_HEIGHT });
}

const PX_PER_IN = 96;
const BAND_CONTENT_PATHS = ["elements", "shapes", "qrCodes", "images"];

// Moves every positioned item of a section sideways by `dx` px (never left
// of 0). Total and footer positions are measured from the paper edge in the
// four-section layout, but from inside the margins in bands mode.
function shiftSection(section, dx) {
  if (!section || !dx) return section;
  const next = { ...section };
  BAND_CONTENT_PATHS.forEach((path) => {
    if (!section[path]) return;
    next[path] = Object.fromEntries(Object.entries(section[path]).map(([key, item]) => {
      if (!item || !item.style || item.style.left === undefined || key === "border") return [key, item];
      const left = Math.max(0, (parseFloat(item.style.left) || 0) + dx);
      return [key, { ...item, style: { ...item.style, left: `${Math.round(left)}px` } }];
    }));
  });
  if (section.VatSummary?.style?.left !== undefined) {
    const left = Math.max(0, (parseFloat(section.VatSummary.style.left) || 0) + dx);
    next.VatSummary = { ...section.VatSummary, style: { ...section.VatSummary.style, left: `${Math.round(left)}px` } };
  }
  return next;
}

// Changes a four-section template to bands mode without moving anything on
// the page. Returns { prop, bands, total, foot } to store.
//
// In bands mode every band sits inside the margins, so the header band is
// its section minus the top margin (its content already sat inside the
// margins), the footer band its section minus the bottom margin, and total
// and footer content moves left by the left margin.
export function convertToBands(template) {
  const margin = template?.prop?.margin || {};
  const top = (Number(margin.top) || 0) * PX_PER_IN;
  const bottom = (Number(margin.bottom) || 0) * PX_PER_IN;
  const left = (Number(margin.left) || 0) * PX_PER_IN;

  const bands = sectionsToBands(template).map((band) => {
    if (band.id === "head") return { ...band, height: Math.max(20, band.height - top) };
    if (band.id === "foot") return { ...band, height: Math.max(20, band.height - bottom) };
    return band;
  });

  return {
    prop: { ...template.prop, layoutMode: LAYOUT_MODES.BANDS, layoutVersion: LAYOUT_VERSION },
    bands,
    total: shiftSection(template.total, -left),
    foot: shiftSection(template.foot, -left),
  };
}

// The reverse of convertToBands, or null when the bands can't be written as
// the four sections. Returns { prop, heights, head, total, foot } to store.
export function convertToSections(template) {
  const margin = template?.prop?.margin || {};
  const top = (Number(margin.top) || 0) * PX_PER_IN;
  const bottom = (Number(margin.bottom) || 0) * PX_PER_IN;
  const left = (Number(margin.left) || 0) * PX_PER_IN;
  const pageHeightPx = getPageSizePx(template?.prop).height;

  const bands = getTemplateBands(template).map((band) => {
    if (band.id === "head") return { ...band, height: band.height + top };
    if (band.id === "foot") return { ...band, height: band.height + bottom };
    return band;
  });
  if (usesBandOnlyFeatures(template)) return null;
  const sections = bandsToSections(bands, { pageHeightPx });
  if (!sections) return null;

  const { layoutMode, layoutVersion, ...prop } = template.prop || {};
  return {
    prop: { ...prop, layoutMode: LAYOUT_MODES.SECTIONS },
    heights: sections.heights,
    head: { ...template.head, printingHeaderEveryPage: sections.printingHeaderEveryPage },
    total: { ...shiftSection(template.total, left), position: sections.totalPosition },
    foot: { ...shiftSection(template.foot, left), printingFooterEveryPage: sections.printingFooterEveryPage },
  };
}

// The four sections a bands template falls back to for PDF services without
// bands support (spec §9): the header, total and footer bands as today's
// sections, positions moved back to their four-section origin, heights from
// those bands. Bands it can't express are left out. Returns { prop, heights,
// head, total, foot }.
export function toFallbackSections(template) {
  const exact = convertToSections({ ...template, pageFrame: null, bands: (template?.bands || []).map((b) => ({ ...b, canGrow: false, detailRow: undefined })) });
  if (exact) return exact;

  const margin = template?.prop?.margin || {};
  const top = (Number(margin.top) || 0) * PX_PER_IN;
  const bottom = (Number(margin.bottom) || 0) * PX_PER_IN;
  const left = (Number(margin.left) || 0) * PX_PER_IN;
  const pageHeightPx = getPageSizePx(template?.prop).height;
  const bands = getTemplateBands(template);
  const bySection = (key) => bands.find((band) => band.section === key);
  const head = bySection("head");
  const total = bySection("total");
  const foot = bySection("foot");

  const pct = (px) => (px / pageHeightPx) * 100;
  const heights = {
    head: head ? pct(head.height + top) : pct(top),
    total: total ? pct(total.height) : 0,
    foot: foot ? pct(foot.height + bottom) : pct(bottom),
  };
  heights.body = Math.max(0, 100 - heights.head - heights.total - heights.foot);

  return {
    prop: template.prop,
    heights,
    head: { ...template.head, printingHeaderEveryPage: !head || head.repeat === BAND_REPEAT.EVERY_PAGE },
    total: { ...shiftSection(template.total, left), position: total && total.placement === BAND_PLACEMENT.PAGE_BOTTOM ? "aboveFooter" : "afterItems" },
    foot: { ...shiftSection(template.foot, left), printingFooterEveryPage: !!foot && foot.repeat === BAND_REPEAT.EVERY_PAGE },
  };
}

// Whether a template has to be saved in the bands format: it is in bands
// mode and uses something the four sections can't express. Templates that
// don't keep saving in today's format, so their PDFs can't change.
export function requiresBandsFormat(template) {
  return getLayoutMode(template) === LAYOUT_MODES.BANDS && convertToSections(template) === null;
}
